import { gql } from "@apollo/client";
import { CONTENT_LIBRARY_REFETCH_QUERIES } from "@src/lib/contexts/TagContext/TagContext";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import { Label } from "@src/lib/ui/Label";
import { useToast } from "@src/lib/hooks/useToast";
import { route } from "nextjs-routes";
import { FC, useState } from "react";
import { useCopyModuleMutation } from "./CopyModuleDialog.generated";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

type Props = {
  moduleId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
const CopyModuleDialog: FC<Props> = ({ moduleId, open, onOpenChange }) => {
  const { addToast, addErrorToast } = useToast();

  const [duplicateChildContent, setDuplicateChildContent] = useState(false);
  const [copyModule, { loading }] = useCopyModuleMutation({
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
    onCompleted: (data) => {
      if (data.copyModule.success && data.copyModule.module?.id) {
        addToast({
          message: `Module successfully duplicated! Only the structure of the module has been duplicated. The content within the module has not been duplicated.`,
        });
        onOpenChange(false);
        window.open(
          route({
            pathname: "/library/library-item/[id]",
            query: { id: data.copyModule.module.libraryItem?.id },
          }),
        );
      } else {
        addErrorToast({
          ...data.copyModule,
          callsite: "course_card_copy_course",
        });
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[700px]">
        <DialogHeader className="mb-4">
          <DialogTitle>Duplicate Module</DialogTitle>
        </DialogHeader>
        <RadioGroup
          value={duplicateChildContent ? "copy" : "reuse"}
          onValueChange={(val) => setDuplicateChildContent(val === "copy")}
          className="flex flex-col gap-2 self-stretch"
        >
          <div className="flex shrink-0 items-center gap-2">
            <RadioGroupItem value="reuse" id="reuse" />
            <Label htmlFor="reuse" className="cursor-pointer font-normal">
              Duplicate module structure and reuse courses, check-ins, and
              resources
            </Label>
          </div>
          <div className="mb-4 ml-6 flex flex-col gap-1">
            <ul className="list-disc pl-5">
              <li className="text-gray-7">
                <Text type="P2" className="text-gray-7" multiline>
                  This duplicates the structure of the module AND pulls in the
                  original version of the courses, check-ins and resources
                  inside.
                </Text>
              </li>
              <li className="text-gray-7">
                <Text type="P2" className="text-gray-7" multiline>
                  When making edits to content within the module, it will update
                  that content anywhere else it lives in Opus.
                </Text>
              </li>
            </ul>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <RadioGroupItem value="copy" id="copy" />
            <Label htmlFor="copy" className="cursor-pointer font-normal">
              Duplicate module structure and make a copy of all courses,
              check-ins and resources
            </Label>
          </div>
          <div className="mb-4 ml-6 flex flex-col gap-1">
            <ul className="list-disc pl-5">
              <li className="text-gray-7">
                <Text type="P2" className="text-gray-7" multiline>
                  This duplicates the structure of the module AND makes a copy
                  version of the original courses, check-ins and resources.
                </Text>
              </li>
              <li className="text-gray-7">
                <Text type="P2" className="text-gray-7" multiline>
                  You can edit the copied version of the content and it will not
                  make changes to the original version.
                </Text>
              </li>
            </ul>
          </div>
        </RadioGroup>
        <DialogFooter className="mt-4">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              loading={loading}
              className="flex-1"
              onClick={() => {
                copyModule({
                  variables: {
                    id: moduleId,
                    duplicateChildContent,
                  },
                });
              }}
            >
              Duplicate
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyModuleDialog;

gql`
  mutation CopyModule($id: Int!, $duplicateChildContent: Boolean!) {
    copyModule(id: $id, duplicateChildContent: $duplicateChildContent) {
      success
      module {
        id
        libraryItem {
          id
        }
      }
      error {
        code
      }
    }
  }
`;
